import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import {
  UserMinus, Plus, AlertTriangle, CheckCircle2, Clock, FileText, ChevronRight,
  Briefcase, Users as UsersIcon, Wallet, ClipboardList, Shield, MessageSquare, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

const EXIT_STATUS_META = {
  initiated: { label: 'Initiated', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  in_clearance: { label: 'In Clearance', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  approved: { label: 'Approved', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200' },
  fnf_processed: { label: 'F&F Processed', color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200' },
};

const ExitManagementPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [pips, setPips] = useState([]);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initiate Exit dialog
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateForm, setInitiateForm] = useState({
    employee_id: '', exit_type: 'resignation', resignation_date: new Date().toISOString().slice(0, 10),
    proposed_last_working_date: '', notice_required_days: 30, notice_served_days: 0,
    reason: '', notes: '',
  });

  // Detail drawer
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [fnfPreview, setFnfPreview] = useState(null);
  const [fnfOverride, setFnfOverride] = useState({ leave_encashment: '', gratuity: '', notice_recovery: '' });

  // PIP dialog
  const [pipOpen, setPipOpen] = useState(false);
  const [pipForm, setPipForm] = useState({
    employee_id: '', start_date: new Date().toISOString().slice(0, 10), end_date: '',
    duration_weeks: 4, concerns: '', goals: '', manager_name: '',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [recs, ps, st, emps] = await Promise.all([
        axios.get(`${API_URL}/api/exit/records`, { withCredentials: true }),
        axios.get(`${API_URL}/api/exit/pips`, { withCredentials: true }),
        axios.get(`${API_URL}/api/exit/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
      ]);
      setRecords(recs.data);
      setPips(ps.data);
      setStats(st.data);
      setEmployees(emps.data || []);
    } catch (err) {
      console.error('Fetch exit data error:', err);
      toast.error('Failed to load exit data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Preselect from URL (?employee_id=...) — used by Employees page "Offboard" button
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const empId = params.get('employee_id');
    if (empId) {
      setInitiateForm(prev => ({ ...prev, employee_id: empId }));
      setInitiateOpen(true);
    }
  }, [location.search]);

  // ===== Initiate Exit =====
  const handleInitiate = async () => {
    if (!initiateForm.employee_id) {
      toast.error('Select an employee');
      return;
    }
    if (!initiateForm.proposed_last_working_date) {
      toast.error('Proposed last working date is required');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/exit/records`, initiateForm, { withCredentials: true });
      toast.success('Exit initiated');
      setInitiateOpen(false);
      // Clear query string
      navigate('/exit-management', { replace: true });
      fetchAll();
      // Open the detail drawer for this new record
      setTimeout(() => openDetail(res.data), 300);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate exit');
    }
  };

  // ===== Detail drawer =====
  const openDetail = async (record) => {
    setSelectedRecord(record);
    setFnfPreview(null);
    setFnfOverride({ leave_encashment: '', gratuity: '', notice_recovery: '' });
    setDetailOpen(true);
  };

  const refreshRecord = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/exit/records/${id}`, { withCredentials: true });
      setSelectedRecord(res.data);
      // Also refresh list
      fetchAll();
    } catch (err) { /* ignore */ }
  };

  const updateRecord = async (patch) => {
    if (!selectedRecord) return;
    try {
      const res = await axios.put(`${API_URL}/api/exit/records/${selectedRecord.id}`, patch, { withCredentials: true });
      setSelectedRecord(res.data);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const toggleClearanceItem = (idx) => {
    if (!selectedRecord) return;
    const clearance = [...(selectedRecord.clearance || [])];
    const cur = clearance[idx];
    const next = cur.status === 'done' ? 'pending' : 'done';
    clearance[idx] = { ...cur, status: next, completed_at: next === 'done' ? new Date().toISOString() : '' };
    updateRecord({ clearance });
  };

  const toggleAccess = (key) => {
    if (!selectedRecord) return;
    const access = { ...(selectedRecord.access_revoked || {}) };
    access[key] = !access[key];
    updateRecord({ access_revoked: access });
  };

  const updateKT = (patch) => {
    if (!selectedRecord) return;
    const kt = { ...(selectedRecord.kt || {}), ...patch };
    updateRecord({ kt });
  };

  const updateExitInterview = (patch) => {
    if (!selectedRecord) return;
    const ei = { ...(selectedRecord.exit_interview || {}), ...patch };
    updateRecord({ exit_interview: ei });
  };

  const loadFnfPreview = async () => {
    if (!selectedRecord) return;
    try {
      const res = await axios.get(`${API_URL}/api/exit/records/${selectedRecord.id}/fnf-preview`, { withCredentials: true });
      setFnfPreview(res.data);
      setFnfOverride({
        leave_encashment: String(res.data.leave_encashment ?? 0),
        gratuity: String(res.data.gratuity ?? 0),
        notice_recovery: String(res.data.notice_recovery ?? 0),
      });
      toast.success('F&F preview loaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load F&F preview');
    }
  };

  const handleCompleteExit = async () => {
    if (!selectedRecord) return;
    if (!window.confirm(`Finalize exit for ${selectedRecord.employee_name}? This will deactivate the account, revoke SSO, and generate the final payslip.`)) return;
    const fnfPayload = fnfPreview ? {
      years_of_service: fnfPreview.years_of_service,
      leave_encashment: parseFloat(fnfOverride.leave_encashment || 0) || 0,
      leave_encashment_days: fnfPreview.leave_encashment_days || 0,
      gratuity: parseFloat(fnfOverride.gratuity || 0) || 0,
      notice_required_days: selectedRecord.notice_required_days,
      notice_served_days: selectedRecord.notice_served_days,
      notice_recovery: parseFloat(fnfOverride.notice_recovery || 0) || 0,
    } : null;
    try {
      const res = await axios.post(
        `${API_URL}/api/exit/records/${selectedRecord.id}/complete`,
        { fnf: fnfPayload, generate_final_payslip: true },
        { withCredentials: true },
      );
      toast.success(res.data.message || 'Exit completed');
      setSelectedRecord(res.data.exit_record);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete exit');
    }
  };

  const handleCancelExit = async () => {
    if (!selectedRecord) return;
    if (!window.confirm('Cancel this exit record? The employee will remain active.')) return;
    try {
      await axios.delete(`${API_URL}/api/exit/records/${selectedRecord.id}`, { withCredentials: true });
      toast.success('Exit cancelled');
      setDetailOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel exit');
    }
  };

  const handleHardDelete = async (record) => {
    if (!record) return;
    if (!window.confirm(`Permanently delete exit record for ${record.employee_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/exit/records/${record.id}/permanent`, { withCredentials: true });
      toast.success('Exit record deleted permanently');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete exit record');
    }
  };

  // ===== PIP =====
  const handleCreatePip = async () => {
    if (!pipForm.employee_id) {
      toast.error('Select an employee');
      return;
    }
    try {
      const payload = {
        ...pipForm,
        goals: pipForm.goals.split('\n').map(g => g.trim()).filter(Boolean),
      };
      await axios.post(`${API_URL}/api/exit/pips`, payload, { withCredentials: true });
      toast.success('PIP created');
      setPipOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create PIP');
    }
  };

  const updatePipStatus = async (pipId, status) => {
    try {
      await axios.put(`${API_URL}/api/exit/pips/${pipId}`, { status }, { withCredentials: true });
      toast.success('PIP updated');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update PIP');
    }
  };

  // Clearance progress %
  const clearancePct = (record) => {
    const items = record.clearance || [];
    if (!items.length) return 0;
    const done = items.filter(i => i.status === 'done').length;
    return Math.round((done / items.length) * 100);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="exit-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UserMinus className="w-7 h-7 text-rose-500" />
            Exit Management
          </h1>
          <p className="text-muted-foreground">Single source of truth for PIP, Resignation, Clearance, KT, F&F and Exit Interviews</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPipOpen(true)} className="gap-2" data-testid="new-pip-btn">
            <AlertTriangle className="w-4 h-4" /> New PIP
          </Button>
          <Button onClick={() => setInitiateOpen(true)} className="gap-2" data-testid="initiate-exit-btn">
            <Plus className="w-4 h-4" /> Initiate Exit
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="exit-stats">
          <StatTile icon={<UserMinus />} label="Total Exits" value={stats.total_exits} />
          <StatTile icon={<Clock />} label="In Progress" value={stats.in_progress} accent="amber" />
          <StatTile icon={<CheckCircle2 />} label="Completed" value={stats.completed} accent="emerald" />
          <StatTile icon={<AlertTriangle />} label="Active PIPs" value={stats.active_pips} accent="rose" />
          <StatTile label="Closed PIPs" value={stats.closed_pips} />
        </div>
      )}

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active-exits">Active Exits</TabsTrigger>
          <TabsTrigger value="pips" data-testid="tab-pips">PIP Tracking</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
        </TabsList>

        {/* Active Exits */}
        <TabsContent value="active" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">In-Progress Exits</CardTitle>
              <CardDescription>Exits that haven't been finalized yet — click any row to manage clearance, KT, and F&F</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? <p className="text-center py-6 text-muted-foreground">Loading...</p> : (
                <ExitRecordsTable
                  records={records.filter(r => !['completed', 'cancelled'].includes(r.status))}
                  onOpen={openDetail}
                  clearancePct={clearancePct}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PIP */}
        <TabsContent value="pips" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Performance Improvement Plans</CardTitle>
              <CardDescription>Track at-risk employees on formal PIPs — outcomes feed into Exit decisions</CardDescription>
            </CardHeader>
            <CardContent>
              {pips.length === 0 ? (
                <p className="text-center py-6 text-muted-foreground">No PIPs on record.</p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="data-table w-full text-sm" data-testid="pips-table">
                    <thead><tr><th>Employee</th><th>Manager</th><th>Duration</th><th>Concerns</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
                    <tbody>
                      {pips.map(p => (
                        <tr key={p.id}>
                          <td><div className="font-medium">{p.employee_name}</div><div className="text-xs text-muted-foreground">{p.department}</div></td>
                          <td className="text-sm">{p.manager_name || '—'}</td>
                          <td className="text-sm">{p.start_date} → {p.end_date || `+${p.duration_weeks}w`}</td>
                          <td className="text-sm max-w-[280px] truncate" title={p.concerns}>{p.concerns}</td>
                          <td><Badge variant="secondary" className="capitalize">{p.status.replace(/_/g, ' ')}</Badge></td>
                          <td className="text-right">
                            <Select value={p.status} onValueChange={(v) => updatePipStatus(p.id, v)}>
                              <SelectTrigger className="w-[180px] h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="closed_success">Closed — Success</SelectItem>
                                <SelectItem value="closed_failed">Closed — Failed</SelectItem>
                                <SelectItem value="exited">Exited</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Completed */}
        <TabsContent value="completed" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Completed & Cancelled Exits</CardTitle>
            </CardHeader>
            <CardContent>
              <ExitRecordsTable
                records={records.filter(r => ['completed', 'cancelled'].includes(r.status))}
                onOpen={openDetail}
                clearancePct={clearancePct}
                onHardDelete={handleHardDelete}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ====== Initiate Exit Dialog ====== */}
      <Dialog open={initiateOpen} onOpenChange={setInitiateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserMinus className="w-5 h-5 text-rose-500" /> Initiate Exit</DialogTitle>
            <DialogDescription>Start the offboarding lifecycle. You'll be able to manage clearance, KT, and F&F next.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Employee *</Label>
              <Select value={initiateForm.employee_id} onValueChange={v => setInitiateForm({ ...initiateForm, employee_id: v })}>
                <SelectTrigger data-testid="initiate-emp-select"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.status === 'active').map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_id || e.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Exit Type</Label>
                <Select value={initiateForm.exit_type} onValueChange={v => setInitiateForm({ ...initiateForm, exit_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resignation">Resignation</SelectItem>
                    <SelectItem value="termination">Termination</SelectItem>
                    <SelectItem value="contract_end">Contract End</SelectItem>
                    <SelectItem value="retirement">Retirement</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Resignation Date</Label>
                <Input type="date" value={initiateForm.resignation_date} onChange={e => setInitiateForm({ ...initiateForm, resignation_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Proposed Last Working Date *</Label>
                <Input type="date" value={initiateForm.proposed_last_working_date} onChange={e => setInitiateForm({ ...initiateForm, proposed_last_working_date: e.target.value })} data-testid="initiate-lwd" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Notice Required (days)</Label>
                <Input type="number" value={initiateForm.notice_required_days} onChange={e => setInitiateForm({ ...initiateForm, notice_required_days: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Reason</Label>
              <Textarea rows={2} value={initiateForm.reason} onChange={e => setInitiateForm({ ...initiateForm, reason: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInitiateOpen(false)}>Cancel</Button>
            <Button onClick={handleInitiate} data-testid="confirm-initiate-exit">Initiate Exit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== PIP Dialog ====== */}
      <Dialog open={pipOpen} onOpenChange={setPipOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Create PIP</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Employee *</Label>
              <Select value={pipForm.employee_id} onValueChange={v => setPipForm({ ...pipForm, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.status === 'active').map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Manager Name</Label>
                <Input value={pipForm.manager_name} onChange={e => setPipForm({ ...pipForm, manager_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Duration (weeks)</Label>
                <Input type="number" value={pipForm.duration_weeks} onChange={e => setPipForm({ ...pipForm, duration_weeks: parseInt(e.target.value) || 4 })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Start Date</Label>
                <Input type="date" value={pipForm.start_date} onChange={e => setPipForm({ ...pipForm, start_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">End Date</Label>
                <Input type="date" value={pipForm.end_date} onChange={e => setPipForm({ ...pipForm, end_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Concerns</Label>
              <Textarea rows={3} value={pipForm.concerns} onChange={e => setPipForm({ ...pipForm, concerns: e.target.value })} placeholder="What performance issues triggered this PIP?" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Goals (one per line)</Label>
              <Textarea rows={4} value={pipForm.goals} onChange={e => setPipForm({ ...pipForm, goals: e.target.value })} placeholder="Hit at least 90% sprint completion&#10;Complete 5 code reviews per week" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPipOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePip} data-testid="confirm-create-pip">Create PIP</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== Detail Drawer ====== */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
          {selectedRecord && (
            <>
              <SheetHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <SheetTitle className="flex items-center gap-2">
                      <UserMinus className="w-5 h-5 text-rose-500" /> {selectedRecord.employee_name}
                    </SheetTitle>
                    <SheetDescription>
                      {selectedRecord.designation} · {selectedRecord.department} · {selectedRecord.employee_code}
                    </SheetDescription>
                  </div>
                  <Badge className={EXIT_STATUS_META[selectedRecord.status]?.color}>
                    {EXIT_STATUS_META[selectedRecord.status]?.label || selectedRecord.status}
                  </Badge>
                </div>
              </SheetHeader>

              <div className="mt-4">
                {/* Progress bar */}
                <div className="space-y-1 mb-4">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Clearance Progress</span>
                    <span className="font-bold">{clearancePct(selectedRecord)}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${clearancePct(selectedRecord)}%` }} />
                  </div>
                </div>

                <Tabs defaultValue="overview">
                  <TabsList className="flex-wrap h-auto justify-start">
                    <TabsTrigger value="overview" className="gap-1"><FileText className="w-3.5 h-3.5" /> Overview</TabsTrigger>
                    <TabsTrigger value="clearance" className="gap-1" data-testid="tab-clearance"><ClipboardList className="w-3.5 h-3.5" /> Clearance</TabsTrigger>
                    <TabsTrigger value="kt" className="gap-1" data-testid="tab-kt"><UsersIcon className="w-3.5 h-3.5" /> KT</TabsTrigger>
                    <TabsTrigger value="access" className="gap-1" data-testid="tab-access"><Shield className="w-3.5 h-3.5" /> Access</TabsTrigger>
                    <TabsTrigger value="interview" className="gap-1" data-testid="tab-interview"><MessageSquare className="w-3.5 h-3.5" /> Exit Interview</TabsTrigger>
                    <TabsTrigger value="fnf" className="gap-1" data-testid="tab-fnf"><Wallet className="w-3.5 h-3.5" /> F&F</TabsTrigger>
                  </TabsList>

                  {/* Overview */}
                  <TabsContent value="overview" className="mt-4 space-y-3">
                    <KeyValue label="Exit Type" value={selectedRecord.exit_type} capitalize />
                    <KeyValue label="Resignation Date" value={selectedRecord.resignation_date} />
                    <KeyValue label="Proposed Last Working" value={selectedRecord.proposed_last_working_date} />
                    <KeyValue label="Actual Last Working" value={selectedRecord.actual_last_working_date || '—'} />
                    <KeyValue label="Notice (req/served)" value={`${selectedRecord.notice_required_days} / ${selectedRecord.notice_served_days} days`} />
                    {selectedRecord.reason && <div><Label className="text-xs">Reason</Label><p className="text-sm">{selectedRecord.reason}</p></div>}
                    {selectedRecord.notes && <div><Label className="text-xs">Notes</Label><p className="text-sm">{selectedRecord.notes}</p></div>}

                    {selectedRecord.status !== 'completed' && selectedRecord.status !== 'cancelled' && (
                      <div className="grid grid-cols-2 gap-3 pt-2">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Notice Served (days)</Label>
                          <Input type="number" defaultValue={selectedRecord.notice_served_days}
                                 onBlur={(e) => updateRecord({ notice_served_days: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Update Proposed LWD</Label>
                          <Input type="date" defaultValue={selectedRecord.proposed_last_working_date}
                                 onBlur={(e) => updateRecord({ proposed_last_working_date: e.target.value })} />
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Clearance */}
                  <TabsContent value="clearance" className="mt-4 space-y-2">
                    {(selectedRecord.clearance || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No clearance items.</p>
                    ) : (
                      Object.entries(groupBy(selectedRecord.clearance, 'department')).map(([dept, items]) => (
                        <div key={dept} className="rounded-md border p-3">
                          <p className="text-sm font-medium mb-2">{dept}</p>
                          <div className="space-y-1.5">
                            {items.map((item) => {
                              const idx = selectedRecord.clearance.indexOf(item);
                              return (
                                <label key={idx} className="flex items-center gap-3 cursor-pointer text-sm py-1"
                                       data-testid={`clearance-item-${idx}`}>
                                  <Checkbox checked={item.status === 'done'}
                                            onCheckedChange={() => toggleClearanceItem(idx)}
                                            disabled={selectedRecord.status === 'completed' || selectedRecord.status === 'cancelled'} />
                                  <span className={item.status === 'done' ? 'line-through text-muted-foreground' : ''}>{item.item}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </TabsContent>

                  {/* KT */}
                  <TabsContent value="kt" className="mt-4 space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">KT Status</Label>
                      <Select value={selectedRecord.kt?.status || 'not_started'} onValueChange={(v) => updateKT({ status: v })}>
                        <SelectTrigger data-testid="kt-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">Not Started</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="done">Done</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Handover To</Label>
                      <Input defaultValue={selectedRecord.kt?.handover_to || ''} onBlur={(e) => updateKT({ handover_to: e.target.value })} placeholder="Colleague name / team" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">KT Notes / Document Links</Label>
                      <Textarea rows={5} defaultValue={selectedRecord.kt?.notes || ''} onBlur={(e) => updateKT({ notes: e.target.value })}
                                placeholder="Summary of KT sessions, shared docs, recorded videos, etc." />
                    </div>
                  </TabsContent>

                  {/* Access Revocation */}
                  <TabsContent value="access" className="mt-4">
                    <p className="text-xs text-muted-foreground mb-3">Check off accesses as they are revoked. The system also auto-revokes Email + SSO on completion.</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedRecord.access_revoked || {}).map(([key, revoked]) => (
                        <label key={key} className="flex items-center gap-2 p-2 rounded-md border cursor-pointer"
                               data-testid={`access-${key}`}>
                          <Checkbox checked={revoked} onCheckedChange={() => toggleAccess(key)}
                                    disabled={selectedRecord.status === 'completed' || selectedRecord.status === 'cancelled'} />
                          <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                          {revoked && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto" />}
                        </label>
                      ))}
                    </div>
                  </TabsContent>

                  {/* Exit Interview */}
                  <TabsContent value="interview" className="mt-4 space-y-3">
                    <label className="flex items-center gap-2">
                      <Checkbox checked={selectedRecord.exit_interview?.conducted}
                                onCheckedChange={(v) => updateExitInterview({ conducted: !!v, conducted_on: v ? new Date().toISOString().slice(0, 10) : '' })}
                                data-testid="ei-conducted" />
                      <span className="text-sm">Exit interview conducted</span>
                    </label>
                    {selectedRecord.exit_interview?.conducted && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs">Conducted By</Label>
                            <Input defaultValue={selectedRecord.exit_interview?.conducted_by || ''}
                                   onBlur={(e) => updateExitInterview({ conducted_by: e.target.value })} />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Conducted On</Label>
                            <Input type="date" defaultValue={selectedRecord.exit_interview?.conducted_on || ''}
                                   onBlur={(e) => updateExitInterview({ conducted_on: e.target.value })} />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Overall Rating (1-5)</Label>
                          <Input type="number" min="1" max="5" defaultValue={selectedRecord.exit_interview?.rating || ''}
                                 onBlur={(e) => updateExitInterview({ rating: e.target.value ? parseInt(e.target.value) : null })}
                                 className="w-24" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Key Feedback</Label>
                          <Textarea rows={5}
                                    defaultValue={selectedRecord.exit_interview?.responses?.feedback || ''}
                                    onBlur={(e) => updateExitInterview({ responses: { ...selectedRecord.exit_interview?.responses, feedback: e.target.value } })}
                                    placeholder="What went well? What could improve? Reasons for leaving..." />
                        </div>
                      </>
                    )}
                  </TabsContent>

                  {/* F&F */}
                  <TabsContent value="fnf" className="mt-4 space-y-3">
                    <Button onClick={loadFnfPreview} variant="outline" className="gap-2" data-testid="load-fnf">
                      <Wallet className="w-4 h-4" /> Load F&F Preview
                    </Button>
                    {fnfPreview && (
                      <div className="space-y-3 pt-2 border-t">
                        <div className="grid grid-cols-2 gap-3">
                          <KeyValue label="Years of Service" value={fnfPreview.years_of_service?.toFixed?.(2) || 0} />
                          <KeyValue label="Available Leave Days" value={fnfPreview.available_leave_days_on_record || 0} />
                          <KeyValue label="Monthly Basic" value={fmtCurrency(fnfPreview.monthly_basic)} />
                          <KeyValue label="Monthly Full Pay" value={fmtCurrency(fnfPreview.monthly_full_pay)} />
                        </div>
                        <div className="space-y-2">
                          <FnfField label="Leave Encashment" hint={`${fnfPreview.leave_encashment_days || 0} days × per-day basic`}
                                    value={fnfOverride.leave_encashment} onChange={v => setFnfOverride({ ...fnfOverride, leave_encashment: v })}
                                    testid="fnf-leave" />
                          <FnfField label="Gratuity"
                                    hint={fnfPreview.years_of_service >= 5 ? '15/26 × basic × completed years' : 'Eligible only after 5 years of service'}
                                    value={fnfOverride.gratuity} onChange={v => setFnfOverride({ ...fnfOverride, gratuity: v })}
                                    testid="fnf-gratuity" />
                          <FnfField label="Notice Recovery (deduction)"
                                    hint={`(notice required − served) × per-day full pay`}
                                    value={fnfOverride.notice_recovery} onChange={v => setFnfOverride({ ...fnfOverride, notice_recovery: v })}
                                    isNegative testid="fnf-notice" />
                        </div>
                        <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-medium">Net F&F Add-on (excl. month salary)</span>
                            <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300" data-testid="fnf-net">
                              {fmtCurrency(
                                (parseFloat(fnfOverride.leave_encashment) || 0) +
                                (parseFloat(fnfOverride.gratuity) || 0) -
                                (parseFloat(fnfOverride.notice_recovery) || 0)
                              )}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Added on top of the pro-rated final-month salary in the Final Payslip.</p>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                {/* Action footer */}
                {selectedRecord.status !== 'completed' && selectedRecord.status !== 'cancelled' && (
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 mt-6 pt-4 border-t">
                    <Button variant="outline" onClick={handleCancelExit} data-testid="cancel-exit">Cancel Exit</Button>
                    <Button onClick={handleCompleteExit} className="bg-rose-500 hover:bg-rose-600 gap-2" data-testid="complete-exit">
                      <CheckCircle2 className="w-4 h-4" /> Finalize & Offboard
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// ====== Helper components ======
const StatTile = ({ icon, label, value, accent }) => {
  const palette = {
    amber: 'text-amber-600 dark:text-amber-300',
    emerald: 'text-emerald-600 dark:text-emerald-300',
    rose: 'text-rose-600 dark:text-rose-300',
  };
  const cls = palette[accent] || 'text-primary';
  return (
    <div className="p-3 rounded-md border bg-muted/30">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon && <span className="w-4 h-4">{icon}</span>}
        <span>{label}</span>
      </div>
      <p className={`text-2xl font-bold mt-1 ${cls}`}>{value}</p>
    </div>
  );
};

const KeyValue = ({ label, value, capitalize }) => (
  <div className="flex justify-between items-center text-sm border-b border-dashed pb-1">
    <span className="text-muted-foreground">{label}</span>
    <span className={capitalize ? 'capitalize font-medium' : 'font-medium'}>{value || '—'}</span>
  </div>
);

const FnfField = ({ label, hint, value, onChange, isNegative, testid }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}{isNegative ? ' (−)' : ''}</Label>
    <Input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

const ExitRecordsTable = ({ records, onOpen, clearancePct, onHardDelete }) => {
  if (records.length === 0) return <p className="text-center py-6 text-muted-foreground">No records.</p>;
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="data-table w-full text-sm" data-testid="exit-records-table">
        <thead>
          <tr><th>Employee</th><th>Type</th><th>LWD</th><th>Status</th><th>Clearance</th><th className="w-10"></th></tr>
        </thead>
        <tbody>
          {records.map(r => (
            <tr key={r.id} className="cursor-pointer hover:bg-muted/40" onClick={() => onOpen(r)} data-testid={`exit-row-${r.id}`}>
              <td>
                <div className="font-medium">{r.employee_name}</div>
                <div className="text-xs text-muted-foreground">{r.department} · {r.employee_code}</div>
              </td>
              <td><Badge variant="outline" className="capitalize">{r.exit_type}</Badge></td>
              <td className="text-sm">{r.actual_last_working_date || r.proposed_last_working_date || '—'}</td>
              <td><Badge className={EXIT_STATUS_META[r.status]?.color}>{EXIT_STATUS_META[r.status]?.label || r.status}</Badge></td>
              <td>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-muted rounded overflow-hidden w-20">
                    <div className="h-full bg-emerald-500" style={{ width: `${clearancePct(r)}%` }} />
                  </div>
                  <span className="text-xs font-mono">{clearancePct(r)}%</span>
                </div>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                {onHardDelete ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                    onClick={() => onHardDelete(r)}
                    data-testid={`hard-delete-exit-${r.id}`}
                    title="Delete permanently"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const groupBy = (arr, key) => {
  return (arr || []).reduce((acc, item) => {
    const k = item[key] || 'Other';
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {});
};

export default ExitManagementPage;
